import type { Field } from '../models/issues/field';
import type { CustomFieldDto } from '../types/api/help-hub-api';

export type CustomFieldsDto = Record<string, CustomFieldDto>;
export type CustomFieldsRecord = Record<string, string | string[]> | undefined;

export const generateBreadcrumb = (parts: Array<string | null | undefined>) => {
  if (!parts || parts.length === 0) {
    return '';
  }

  return parts.filter(Boolean).join(' → ');
};

export const formatDate = (
  dateString: string | undefined,
  timeZone?: string
): string => {
  if (!dateString) {
    return '';
  }

  const date = new Date(dateString);

  return date
    .toLocaleString('en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone,
    })
    .replace(',', '');
};

const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const formatDateOnly = (dateString: string | undefined): string => {
  if (!dateString) {
    return '';
  }

  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return dateString;
  }

  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: DATE_ONLY_PATTERN.test(dateString) ? 'UTC' : undefined,
  });
};

export const toSnakeCase = (str: string): string =>
  str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);

export const toCamelCase = (str: string): string =>
  str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());

export const getInitials = (name: string): string =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .toUpperCase();

const toApiValue = (
  key: string,
  value: string | string[],
  fields: Record<string, Field>
): string | string[] | number => {
  const field = fields[key];

  if (field?.isMulti && !Array.isArray(value)) {
    return [value];
  }

  if (
    field?.dataType === 'number' &&
    !field?.isMulti &&
    typeof value === 'string'
  ) {
    return Number(value);
  }

  return value;
};

const toFrontendValue = (values: unknown): string | string[] | undefined => {
  if (typeof values === 'number') {
    return values.toString();
  }

  if (typeof values === 'string') {
    return values;
  }

  if (
    Array.isArray(values) &&
    values.every((value) => typeof value === 'string')
  ) {
    return values;
  }

  return undefined;
};

export const transformValuesToApi = (
  obj: CustomFieldsRecord,
  fields: Record<string, Field> = {}
): CustomFieldsDto | undefined => {
  if (!obj) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      toApiValue(key, value, fields),
    ])
  ) as CustomFieldsDto;
};

export const transformValuesToFrontend = (
  obj: CustomFieldsDto | undefined
): CustomFieldsRecord => {
  if (!obj) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, value]) => value !== null && value !== undefined)
      .map(([key, value]) => [key, toFrontendValue(value)])
      .filter(([, value]) => value !== undefined)
  ) as CustomFieldsRecord;
};

// a secure and enhanced alternative to Math.random()
// returns a random number in the range [min, max] (inclusive)
export const generateRandomNumber = (min = 0, max = 1): number => {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return (array[0] / 0xffffffff) * (max - min) + min;
};

export const generateSubmissionId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `submission-${Date.now()}-${Math.floor(generateRandomNumber(0, 0xffffffff)).toString(16).padStart(8, '0')}`;
};
